import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { fetchTimeline } from '../../../../services/timeline';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import type {
    ClinicalFormData,
    ClinicalWizardConfig
} from '../../../patient-files/actions/clinical/clinicalWizardConfig';
import {
    formDataToFields,
    extractVcaNoteFromEvent
} from '../../../patient-files/actions/clinical/clinicalEventFields';
import { ClinicalFieldRecap } from '../../../patient-files/actions/clinical/ClinicalFieldRecap';
import { EMPTY_TIPTAP_DOC } from '../../../patient-files/actions/dx-report/templates';
import { applyResolvedUrlsToDoc } from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import { resolveDocImagesInBackground } from '../../../../utils/docImageResolution';
import { useDocumentVirtualAppointmentRecap } from '../../../virtual-appointments/useDocumentVirtualAppointmentRecap';

const STALE_MS = 60_000;

interface ClinicalDocPanelProps<T extends ClinicalFormData> {
    patientId: string;
    /** ISO cutoff (case phaseEnteredAt) — only submissions from the current phase. */
    since?: string | null;
    /** Document label, e.g. 'RDI' or 'SDI'. */
    title: string;
    /** Timeline event type, e.g. 'rdi_submission' or 'sdi_submission'. */
    timelineType: string;
    wizardConfig: ClinicalWizardConfig<T>;
    /** Actions-tab deep-link action, e.g. 'rdi' or 'sdi'. */
    openAction: string;
    emptyText: string;
}

export function ClinicalDocPanel<T extends ClinicalFormData>({
    patientId,
    since = null,
    title,
    timelineType,
    wizardConfig,
    openAction,
    emptyText
}: ClinicalDocPanelProps<T>) {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const {
        data: events,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['clinical-doc-panel', timelineType, patientId, since],
        queryFn: async (): Promise<TimelineEvent[]> => {
            const res = await fetchTimeline(patientId, {
                type: timelineType,
                isLatest: true,
                limit: 50
            });
            const sinceMs = since ? new Date(since).getTime() : null;
            return res.data
                .filter((e) => sinceMs === null || new Date(e.createdAt).getTime() >= sinceMs)
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        },
        staleTime: STALE_MS,
        enabled: Boolean(patientId)
    });

    // Default to the latest document; respect an explicit selection while it exists.
    const selected = events?.find((e) => e.id === selectedId) ?? events?.[0] ?? null;
    const formData = useMemo(
        () => (selected ? wizardConfig.extractFormDataFromEvent(selected) : null),
        [selected, wizardConfig]
    );
    const virtualAppointments = useDocumentVirtualAppointmentRecap(patientId, selected?.groupId);

    // Resolve any image refs embedded in the instructions doc (same as the wizard).
    const [notesDoc, setNotesDoc] = useState<TiptapDoc>(EMPTY_TIPTAP_DOC);
    useEffect(() => {
        const base = formData?.notesDoc ?? EMPTY_TIPTAP_DOC;
        setNotesDoc(base);
        return resolveDocImagesInBackground(
            patientId,
            [base],
            'ClinicalDocPanel.resolveNoteImageUrls',
            (urls) => setNotesDoc((prev) => applyResolvedUrlsToDoc(prev, urls))
        );
    }, [formData, patientId]);

    return (
        <div className="h-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-sm text-gray-800">{title}</span>
                    {events && events.length > 1 && (
                        <div className="flex items-center gap-1">
                            {events.map((e, i) => {
                                const isSel = e.id === selected?.id;
                                return (
                                    <button
                                        key={e.id}
                                        type="button"
                                        onClick={() => setSelectedId(e.id)}
                                        className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-colors ${
                                            isSel
                                                ? 'border-gray-800 bg-gray-800 text-white'
                                                : 'border-gray-300 text-gray-600 bg-white hover:border-gray-400'
                                        }`}
                                    >
                                        v{events.length - i}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <a
                    href={`/patients/${encodeURIComponent(patientId)}?tab=actions&action=${openAction}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 flex-shrink-0 group"
                >
                    <span className="text-sm text-gray-800 group-hover:text-gray-600 transition-colors">
                        Open {title}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </a>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 scrollbar-hide">
                {isLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 px-2 py-2">
                        <span className="h-3 w-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                        Loading {title}s…
                    </div>
                ) : isError ? (
                    <div className="text-sm text-red-500 px-2 py-2">
                        Couldn't load the {title}(s).
                    </div>
                ) : !events || events.length === 0 ? (
                    <div className="flex items-center gap-2 px-2 py-2">
                        <span className="h-2 w-2 rounded-full bg-gray-300" />
                        <span className="text-sm text-gray-500">{emptyText}</span>
                    </div>
                ) : !formData || !selected ? (
                    <div className="text-sm text-gray-500 px-2 py-2">
                        Couldn't read this {title}'s details.
                    </div>
                ) : (
                    <ClinicalFieldRecap
                        fields={formDataToFields(formData)}
                        elasticsSchemes={wizardConfig.getElasticsSchemes(formData)}
                        legacyElastics={formData.legacyElastics}
                        nextAppointment={wizardConfig.getNextAppointment(formData)}
                        notesDoc={notesDoc}
                        legacyVcaNote={extractVcaNoteFromEvent(selected)}
                        virtualAppointments={virtualAppointments}
                    />
                )}
            </div>
        </div>
    );
}
